import { Component, OnInit } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { TrialSubscriptionService } from '../trial-subscription.service';
import { AlertComponent } from '../../alert/alert.component';

@Component({
  selector: 'app-create-trial-company',
  templateUrl: './create-trial-company.component.html',
  styleUrls: ['./create-trial-company.component.css']
})
export class CreateTrialCompanyComponent implements OnInit {
  trialCompanyForm: FormGroup;
  pattern = "^[a-zA-Z0-9][a-zA-Z0-9\-\_\.\,\)\(''\"\n ]*$";
  
  // Hardcoded values
  private readonly HARDCODED_VALUES = {
    RegistrationIDType: 'CIN',
    RegistrationNumber: 'L17110MH1973PLC019787',
    IndustryCode: '1',
    IndustryType: '1',
    FinCode: null,
    HousingCode: null,
    CIN: 'LBTPK7060D',
    ComplianceEngine: 'DC',
    ComplianceEngineUrl: 'https://int.digicompany.in/gapi/api/v1/secure/',
    ChatEngineURL: '',
    IsGEMSubscribedFlag: true,
    GEMAccessURL: 'https://int.digicompany.in/gem/api/',
    CompanyLogoImage: '',
    ISIN: 'INE002A01088',
    ExchangeCode: 'Exchange Code',
    IsActive: true,
    CompanySecurityCode: 'DGCMP001',
    CompanySecurityName: 'DigiCompany',
    IsADSubscribed: false,
    ADServersDetails: [],
    ClientLoginAPIUrl: 'https://int.digicompany.in/gapi/api/v1/',
    IsFree: true,
    SubsidiaryCompanies: []
  };
  
  // Dropdown options
  typesOfCompany: any[] = [
    { value: 'Public', label: 'Public Limited Company' },
    { value: 'Private', label: 'Private Limited Company' },
    { value: 'LLP', label: 'Limited Liability Partnership' },
    { value: 'Partnership', label: 'Partnership Firm' },
    { value: 'Sole', label: 'Sole Proprietorship' }
  ];
  
  categoriesOfCompany: any[] = [
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Service', label: 'Service' },
    { value: 'Trading', label: 'Trading' },
    { value: 'Technology', label: 'Technology' },
    { value: 'Finance', label: 'Finance' }
  ];
  
  registrationIDTypes: any[] = [
    { value: 'CIN', label: 'Corporate Identification Number (CIN)' },
    { value: 'PAN', label: 'Permanent Account Number (PAN)' },
    { value: 'GST', label: 'GST Registration Number' },
    { value: 'TAN', label: 'Tax Deduction Account Number (TAN)' }
  ];
  
  industryTypes: any[] = [
    { value: 'IT', label: 'Information Technology' },
    { value: 'Manufacturing', label: 'Manufacturing' },
    { value: 'Finance', label: 'Financial Services' },
    { value: 'Healthcare', label: 'Healthcare' },
    { value: 'Education', label: 'Education' }
  ];

  // Store URL parameters for passing to next page
  private urlParams: any = {};

  constructor(
    private companyMasterService: TrialSubscriptionService,
    private router: Router,
    private route: ActivatedRoute,
    private alert: AlertComponent
  ) { }

  ngOnInit(): void {
    // ============================================================
    // STEP 1: Read URL parameters from email link
    // ============================================================
    this.route.queryParams.subscribe(params => {
      this.urlParams = {
        email: params['email'] || '',
        firstName: params['firstName'] || '',
        lastName: params['lastName'] || '',
        phone: params['phone'] || '',
        companyName: params['companyName'] || '',
        companyAddress: params['companyAddress'] || ''
      };
      
      console.log('URL Parameters received:', this.urlParams);
    });
    
    // Calculate default financial year (April 1 to March 31)
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1; // getMonth() returns 0-11, so add 1
    
    let financialYearFrom: string;
    let financialYearTo: string;
    
    if (currentMonth >= 4) {
      // If current month is April or later, financial year starts this year
      financialYearFrom = `${currentYear}-04-01`;
      financialYearTo = `${currentYear + 1}-03-31`;
    } else {
      // If current month is before April, financial year started last year
      financialYearFrom = `${currentYear - 1}-04-01`;
      financialYearTo = `${currentYear}-03-31`;
    }

    this.trialCompanyForm = new FormGroup({
      // Basic Company Information
      companyName: new FormControl('', [Validators.required, Validators.maxLength(100)]),
      symbol: new FormControl('', []),
      
      // Registration Details (Hardcoded - Disabled)
      registrationIDType: new FormControl({value: this.HARDCODED_VALUES.RegistrationIDType, disabled: true}, []),
      registrationNumber: new FormControl({value: this.HARDCODED_VALUES.RegistrationNumber, disabled: true}, []),
      
      // Company Classification
      typeOfCompany: new FormControl('', [Validators.required]),
      categoryOfCompany: new FormControl('', [Validators.required]),
      
      // Address Information
      registeredOfficeAddress: new FormControl('', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]),
      
      // Financial Information
      FinancialYearFrom: new FormControl(financialYearFrom, [Validators.required]),
      FinancialYearTo: new FormControl(financialYearTo, [Validators.required]),
      dateOfIncorporation: new FormControl('', []),
      
      // Listing Information
      listedCompany: new FormControl(true, []),
      equityListing: new FormControl(true, []),
      debtListing: new FormControl(true, []),
      
      // NBFC Information
      nbfcRegulations: new FormControl(false, []),
      acceptsDeposits: new FormControl(true, []),
      
      // Shareholder Information
      noOfShareholders: new FormControl('', []),
      noOfDebentureHolders: new FormControl('', []),
      noOfDepositHolders: new FormControl('', []),
      noOfSecurityHolders: new FormControl('', []),
      
      // Hardcoded Subscription Flags (Disabled)
      isGEMSubscribedFlag: new FormControl({value: this.HARDCODED_VALUES.IsGEMSubscribedFlag, disabled: true}, []),
      GEMAccessURL: new FormControl({value: this.HARDCODED_VALUES.GEMAccessURL, disabled: true}, []),
      complianceEngineURL: new FormControl({value: this.HARDCODED_VALUES.ComplianceEngineUrl, disabled: true}, []),
      chatEngineURL: new FormControl({value: this.HARDCODED_VALUES.ChatEngineURL, disabled: true}, []),
      isADSubscribedFlag: new FormControl({value: this.HARDCODED_VALUES.IsADSubscribed, disabled: true}, []),
      
      // Admin Contact Information - Made optional for testing
      adminFirstName: new FormControl('', []),
      adminLastName: new FormControl('', []),
      adminEmail: new FormControl('', []),
      adminMobileNumber: new FormControl('', []),
      adminDesignation: new FormControl('', [])
    });
    
    // ============================================================
    // STEP 2: Auto-fill Company Name and Registered Office Address from URL parameters
    // ============================================================
    if (this.urlParams.companyName || this.urlParams.companyAddress) {
      this.trialCompanyForm.patchValue({
        companyName: this.urlParams.companyName,
        registeredOfficeAddress: this.urlParams.companyAddress
      });
      console.log('Auto-filled Company Name and Registered Office Address from URL parameters');
    }
  }

  onSubmit() {
    console.log('onSubmit called');
    console.log('Form valid:', this.trialCompanyForm.valid);
    console.log('Form value:', this.trialCompanyForm.value);
    console.log('Form errors:', this.getFormValidationErrors());
    
    if (this.trialCompanyForm.valid) {
      const formData = this.trialCompanyForm.getRawValue(); // getRawValue() includes disabled fields
      
      // Construct payload matching the API requirements
      const payload = {
        CompanyName: formData.companyName,
        DateOfIncorporation: formData.dateOfIncorporation || null,
        TypeOfCompany: formData.typeOfCompany,
        CategoryOfCompany: formData.categoryOfCompany,
        RegisteredOfficeAddress: formData.registeredOfficeAddress,
        RegistrationIDType: this.HARDCODED_VALUES.RegistrationIDType, // hardcoded
        RegistrationNumber: this.HARDCODED_VALUES.RegistrationNumber, // hardcoded
        IndustryCode: this.HARDCODED_VALUES.IndustryCode, // hardcoded
        IndustryType: this.HARDCODED_VALUES.IndustryType, // hardcoded
        FinCode: this.HARDCODED_VALUES.FinCode, // hardcoded
        HousingCode: this.HARDCODED_VALUES.HousingCode, // hardcoded
        EVA: 1,
        FinancialYearFrom: formData.FinancialYearFrom,
        FinancialYearTo: formData.FinancialYearTo,
        NBFCRegulations: formData.nbfcRegulations ? 'true' : 'false',
        AcceptingDepositNBFC: null,
        AcceptsDeposits: formData.acceptsDeposits ? 'true' : 'false',
        AssetSize: null,
        Listed: formData.listedCompany,
        ListingType: this.getListingTypes(formData),
        NumberOfShareholders: parseInt(formData.noOfShareholders) || 0,
        NumberOfDebentureHolders: parseInt(formData.noOfDebentureHolders) || 0,
        NumberOfDepositHolders: parseInt(formData.noOfDepositHolders) || 0,
        NumberOfSecurityHolders: parseInt(formData.noOfSecurityHolders) || 0,
        CIN: this.HARDCODED_VALUES.CIN, // hardcoded
        ComplianceEngine: this.HARDCODED_VALUES.ComplianceEngine, // hardcoded
        ComplianceEngineUrl: this.HARDCODED_VALUES.ComplianceEngineUrl, // hardcoded
        ChatEngineURL: this.HARDCODED_VALUES.ChatEngineURL, // hardcoded
        IsGEMSubscribedFlag: this.HARDCODED_VALUES.IsGEMSubscribedFlag, // hardcoded
        GEMAccessURL: this.HARDCODED_VALUES.GEMAccessURL, // hardcoded
        CompanyLogoImage: this.HARDCODED_VALUES.CompanyLogoImage, // hardcoded
        ISIN: this.HARDCODED_VALUES.ISIN, // hardcoded
        ExchangeCode: this.HARDCODED_VALUES.ExchangeCode, // hardcoded
        Symbol: formData.symbol || '',
        IsActive: this.HARDCODED_VALUES.IsActive, // hardcoded
        CompanySecurityCode: this.HARDCODED_VALUES.CompanySecurityCode, // hardcoded
        CompanySecurityName: this.HARDCODED_VALUES.CompanySecurityName, // hardcoded
        IsADSubscribed: this.HARDCODED_VALUES.IsADSubscribed, // hardcoded
        ADServersDetails: this.HARDCODED_VALUES.ADServersDetails, // hardcoded
        ClientLoginAPIUrl: this.HARDCODED_VALUES.ClientLoginAPIUrl, // hardcoded
        IsFree: this.HARDCODED_VALUES.IsFree, // hardcoded
        SubsidiaryCompanies: this.HARDCODED_VALUES.SubsidiaryCompanies // hardcoded
      };
      
      console.log('Payload:', payload);
      
      // Call the API using CompanyMasterService
      this.companyMasterService.addCompanyTrial(payload)
        .then((response: any) => {
          console.log('API Response:', response);
          const status = response?.status ?? response?.data?.status ?? response?.result?.status;
          const message = response?.message || response?.data?.message || response?.result?.message || 'Operation completed.';

          if (status === 0) {
            // Success: extract and store ids, then navigate
            try {
              const raw = response || {};
              const clientCompanyId = (raw && (raw.ClientCompanyId || raw.clientCompanyId))
                || (raw.data && (raw.data.ClientCompanyId || raw.data.clientCompanyId))
                || (raw.result && (raw.result.ClientCompanyId || raw.result.clientCompanyId));

              const companyName = (raw && (raw.CompanyName || raw.companyName))
                || (raw.data && (raw.data.CompanyName || raw.data.companyName))
                || (raw.result && (raw.result.CompanyName || raw.result.companyName));

              if (clientCompanyId) {
                localStorage.setItem('ClientCompanyId', String(clientCompanyId));
                console.log('Stored ClientCompanyId:', clientCompanyId);
              }
              if (companyName) {
                localStorage.setItem('CompanyName', String(companyName));
                console.log('Stored CompanyName:', companyName);
              }
            } catch (e) {
              console.error('Error extracting/storing company identifiers:', e);
            }

            this.alert.successWithOk1('Company created successfully! Redirecting to create user...', () => {
              $('body').removeClass('modal-open');
              $('.modal-backdrop').remove();
              
              // ============================================================
              // STEP 3: Navigate to create-trial-user with user data as query params
              // ============================================================
              this.router.navigate(['/create-trial-user'], {
                queryParams: {
                  email: this.urlParams.email,
                  firstName: this.urlParams.firstName,
                  lastName: this.urlParams.lastName,
                  phone: this.urlParams.phone
                }
              });
            });
          } else {
            this.alert.error1(message || 'Failed to create company.');
          }
        })
        .catch((error: any) => {
          console.error('Full Error Object:', error);
          console.error('Error Status:', error.status);
          console.error('Error Message:', error.message);
          console.error('Error Response:', error.error);
          console.error('Error URL:', error.url);
          
          let errorMessage = 'Failed to create company. ';
          
          if (error.status === 0) {
            errorMessage += 'Unable to connect to server. Please check if the backend is running.';
          } else if (error.status === 404) {
            errorMessage += 'API endpoint not found.';
          } else if (error.status === 500) {
            errorMessage += 'Server error occurred.';
          } else if (error.status) {
            errorMessage += `HTTP ${error.status}: ${error.message || 'Unknown error'}`;
          } else {
            errorMessage += 'Network error or server unavailable.';
          }
          
          this.alert.error1(errorMessage);
        });
    } else {
      this.alert.error1('Please fill in all required fields correctly.');
    }
  }
  
  private getListingTypes(formData: any): string[] {
    const listingTypes: string[] = [];
    if (formData.equityListing) {
      listingTypes.push('EquityListing');
    }
    if (formData.debtListing) {
      listingTypes.push('DebtListing');
    }
    return listingTypes;
  }

  onGEMSubscriptionChange(event: any) {
    // This is now disabled/hardcoded, but keeping the method for reference
  }

  onADSubscriptionChange(event: any) {
    // This is now disabled/hardcoded, but keeping the method for reference
  }

  // Helper method to get all form validation errors
  getFormValidationErrors() {
    const errors: any = {};
    Object.keys(this.trialCompanyForm.controls).forEach(key => {
      const controlErrors = this.trialCompanyForm.get(key)?.errors;
      if (controlErrors) {
        errors[key] = controlErrors;
      }
    });
    return errors;
  }
}